import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, ExternalLink, FileText, Loader2, MessageCircle, Pencil, Send, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useApi, rupiah, waLink, errMsg } from "@/lib/api";
import { StatusBadge } from "@/components/admin/OrdersTab";
import EditOrderForm from "@/components/admin/EditOrderForm";
import DeleteOrderButton from "@/components/admin/DeleteOrderButton";

const receiptUrl = (o) => (o.receipt_token ? `${window.location.origin}/kuitansi/${o.receipt_token}` : "");

const waMessage = (o, status) => {
  const head = `Halo ${o.parent_name}, kami dari *LENSA JUARA*.\nPesanan *${o.order_code}* (atlet ${o.athlete_name}${o.bib_number ? `, BIB ${o.bib_number}` : ""}, ${o.event_count} mata lomba, ${rupiah(o.total)})`;
  const kw = receiptUrl(o) ? `\n\nKuitansi resmi: ${receiptUrl(o)}` : "";
  if (status === "terverifikasi") return `${head} telah *TERVERIFIKASI*. Terima kasih! Foto akan segera kami kirimkan melalui WhatsApp ini.${kw}`;
  if (status === "ditolak") return `${head} *belum dapat diverifikasi*.${o.admin_note ? `\nAlasan: ${o.admin_note}` : ""}\nMohon kirim ulang bukti transfer yang valid. Terima kasih.`;
  if (status === "terkirim") return `${head} sudah kami kirimkan fotonya. Terima kasih telah mempercayakan momen juara kepada LENSA JUARA!${kw}`;
  return `${head} sedang kami cek.`;
};

function Proof({ order }) {
  const api = useApi();
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    api("get", `/admin/orders/${order.id}/proof`, null, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => setUrl("error"));
    return () => u && URL.revokeObjectURL(u);
  }, [api, order.id]);
  if (!url) return <div className="h-48 flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  if (url === "error") return <p className="text-red-400 text-sm">Gagal memuat bukti</p>;
  return (
    <div>
      {order.proof_content_type === "application/pdf"
        ? <iframe title="bukti" src={url} className="w-full h-72 bg-white" />
        : <img src={url} alt="Bukti transfer" data-testid="proof-image" className="w-full max-h-80 object-contain bg-black" />}
      <a href={url} target="_blank" rel="noreferrer" data-testid="proof-open-link" className="text-xs text-[#FF5A00] flex items-center gap-1 mt-2"><ExternalLink className="w-3 h-3" /> Buka penuh</a>
    </div>
  );
}

export default function OrderDetailDialog({ order, onClose, canVerify, isAdmin, onUpdated, onDeleted }) {
  const api = useApi();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(false);
  useEffect(() => setNote(order?.admin_note || ""), [order]);
  useEffect(() => setEditing(false), [order?.id]);
  if (!order) return null;

  const setStatus = async (status) => {
    setBusy(status);
    try {
      const { data } = await api("patch", `/admin/orders/${order.id}/status`, { status, admin_note: note });
      toast.success("Status diperbarui");
      onUpdated(data);
      window.open(waLink(data.whatsapp, waMessage(data, status)), "_blank");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const btn = (s, label, Icon, cls) => (
    <button disabled={!!busy} onClick={() => setStatus(s)} data-testid={`status-btn-${s}`} className={`flex-1 min-w-[140px] py-2.5 px-3 font-display font-bold uppercase flex items-center justify-center gap-2 active:scale-95 transition-[background-color,transform] duration-150 disabled:opacity-60 ${cls}`}>
      {busy === s ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />} {label}
    </button>
  );

  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="order-detail-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#0d0d0d] border-white/10 rounded-none">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-3xl flex items-center gap-3">{order.order_code} <StatusBadge status={order.status} /></DialogTitle>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-6">
          {editing ? (
            <EditOrderForm order={order} onCancel={() => setEditing(false)} onSaved={(o) => { setEditing(false); onUpdated(o); }} />
          ) : (
          <div className="space-y-3 text-sm">
            {(canVerify || isAdmin) && (
              <div className="flex flex-wrap gap-2">
                {canVerify && (
                  <button onClick={() => setEditing(true)} data-testid="edit-order-btn" className="inline-flex items-center gap-1.5 text-[#FF5A00] border border-[#FF5A00]/40 hover:bg-[#FF5A00]/10 px-3 py-1 text-xs font-semibold uppercase transition-colors">
                    <Pencil className="w-3 h-3" /> Edit Pesanan
                  </button>
                )}
                {isAdmin && <DeleteOrderButton order={order} onDeleted={onDeleted} />}
              </div>
            )}
            <Info k="Pemesan" v={order.parent_name} /><Info k="WhatsApp" v={order.whatsapp} />
            <Info k="Atlet" v={order.athlete_name} /><Info k="No. BIB" v={<b>{order.bib_number || "-"}</b>} /><Info k="Klub" v={order.club} /><Info k="Kategori" v={order.category} />
            <Info k="Total" v={<b className="text-[#FF5A00]">{rupiah(order.total)}</b>} />
            {order.notes && <Info k="Catatan" v={order.notes} />}
            <div className="border-t border-white/10 pt-3 space-y-2">
              {order.events.map((e, i) => (
                <div key={i} className="bg-[#121212] border border-white/10 p-3" data-testid={`detail-event-${i}`}>
                  <p className="font-semibold">{e.name} <span className="text-[#FF5A00]">· {e.day}</span></p>
                  <p className="text-neutral-400 break-words">{e.photos}</p>
                </div>
              ))}
            </div>
            {order.edited_by && <p className="text-xs text-neutral-500" data-testid="edited-by">Terakhir diedit oleh {order.edited_by}</p>}
          </div>
          )}
          <div><p className="text-xs uppercase text-neutral-400 mb-2">Bukti Transfer</p><Proof order={order} /></div>
        </div>
        <div className="border-t border-white/10 pt-4 space-y-3">
          <a href={waLink(order.whatsapp, waMessage(order, order.status))} target="_blank" rel="noreferrer" data-testid="wa-contact-btn"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 px-4 py-2 font-display font-bold uppercase transition-colors">
            <MessageCircle className="w-4 h-4" /> Chat WhatsApp Pembeli
          </a>
          {["terverifikasi", "terkirim"].includes(order.status) && order.receipt_token && (
            <a href={receiptUrl(order)} target="_blank" rel="noreferrer" data-testid="view-receipt-btn"
              className="inline-flex items-center gap-2 ml-2 border border-white/20 hover:border-[#FF5A00] px-4 py-2 font-display font-bold uppercase transition-colors">
              <FileText className="w-4 h-4" /> Lihat Kuitansi
            </a>
          )}
          {canVerify ? (
            <>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan untuk pembeli (opsional, cth. alasan penolakan)" data-testid="admin-note-input" className="bg-[#121212]" />
              <div className="flex flex-wrap gap-2">
                {btn("terverifikasi", "Verifikasi + WA", Check, "bg-[#FF5A00] hover:bg-[#E04F00]")}
                {btn("terkirim", "Foto Terkirim + WA", Send, "bg-sky-700 hover:bg-sky-600")}
                {btn("ditolak", "Tolak + WA", X, "bg-red-700 hover:bg-red-600")}
              </div>
              <p className="text-xs text-neutral-500">Setelah status diubah, WhatsApp akan terbuka dengan pesan otomatis ke pembeli.</p>
            </>
          ) : <p className="text-xs text-neutral-500" data-testid="readonly-note">Mode Operator: hanya dapat melihat.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Info = ({ k, v }) => (
  <div className="flex gap-3"><span className="w-24 shrink-0 text-neutral-500">{k}</span><span className="break-words">{v}</span></div>
);
